import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

export interface CartParticipant {
  civilite: string;
  nom: string;
  prenom: string;
}

export interface CartSession {
  selectedDateInter: string; // "date – lieu" or ""
  participants: CartParticipant[];
}

export interface CartItemOptions {
  modalite: "Inter" | "Intra" | "Hybride" | "";
  trimestre: "T1" | "T2" | "T3" | "T4" | "";
  nbPersonnes: string;
  lieu: "Paris" | "Bussy-Saint-Georges" | "Fontainebleau" | "Province" | "";
  commentaire: string;
  sessions: CartSession[];
}

export interface CartFormation {
  id: string;
  slug: string;
  title: string;
  duration: string;
  heures?: string;
  category: string;
  pole: string;
  datesInter?: { date: string; lieu: string }[];
  options: CartItemOptions;
}

interface CartContextType {
  items: CartFormation[];
  addItem: (item: Omit<CartFormation, "options">, prefill?: Partial<CartItemOptions>) => void;
  removeItem: (id: string) => void;
  updateItemOptions: (id: string, options: Partial<CartItemOptions>) => void;
  clearCart: () => void;
  isInCart: (id: string) => boolean;
  isCartOpen: boolean;
  setCartOpen: (open: boolean) => void;
}

const defaultOptions: CartItemOptions = { modalite: "", trimestre: "", nbPersonnes: "", lieu: "", commentaire: "", sessions: [] };

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartFormation[]>([]);
  const [isCartOpen, setCartOpen] = useState(false);

  const addItem = useCallback((item: Omit<CartFormation, "options">, prefill?: Partial<CartItemOptions>) => {
    setItems((prev) => prev.some((i) => i.id === item.id) ? prev : [...prev, { ...item, options: { ...defaultOptions, ...prefill } }]);
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateItemOptions = useCallback((id: string, options: Partial<CartItemOptions>) => {
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, options: { ...i.options, ...options } } : i));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const isInCart = useCallback((id: string) => items.some((i) => i.id === id), [items]);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateItemOptions, clearCart, isInCart, isCartOpen, setCartOpen }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
